import React from "react";
import { Box, Grid, Switch, TextField, Typography } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import UploadImage from "./UploadImage";
import { IEditStatusEmailValues, IImagePreviews, IEmailTemplate } from "../../interfaces/order";
import { parseLineBreaksForInput } from "../../utils/text";

export interface IEmailSectionProps {
  currentValues: IEditStatusEmailValues;
  setCurrentValues: React.Dispatch<React.SetStateAction<IEditStatusEmailValues>>;
  template?: IEmailTemplate;
  imagePreviews: IImagePreviews;
  handleUploadedFilePreview: (_id: string, _file: File | Blob) => void;
}

const QUILL_MODULES = {
  toolbar: [
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline"],
    [{ align: [] }],
    ["link"],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

export const LogoSection = ({
  setCurrentValues,
  template,
  imagePreviews,
  handleUploadedFilePreview,
}: Omit<IEmailSectionProps, "currentValues">) => (
  <Grid item xs={12} mb={3}>
    <Box display="flex" justifyContent="space-between">
      <Typography fontSize={20} mb={1}>
        Marca
      </Typography>
    </Box>
    <Typography fontSize={14} mb={2}>
      Edita la marca que verán tus usuarios.
    </Typography>
    <UploadImage
      imagePreview={imagePreviews?.logo}
      imageFromTemplate={template?.logo_url}
      maxWidth={234}
      id="logo"
      width={234}
      height={76}
      setValues={setCurrentValues}
      handleUpload={handleUploadedFilePreview}
    />
  </Grid>
);

export const BannerSection = ({
  setCurrentValues,
  template,
  imagePreviews,
  handleUploadedFilePreview,
}: Omit<IEmailSectionProps, "currentValues">) => (
  <Grid item xs={12} mb={3}>
    <Typography fontSize={20} mb={1}>
      Banner
    </Typography>
    <Typography fontSize={14} mb={2}>
      Edita el banner que verán tus usuarios.
    </Typography>
    <UploadImage
      imagePreview={imagePreviews?.banner}
      imageFromTemplate={template?.banner_url}
      id="banner"
      width={658}
      height={249}
      fullWidth
      setValues={setCurrentValues}
      handleUpload={handleUploadedFilePreview}
    />
  </Grid>
);

export const DescriptionSection = ({
  currentValues,
  setCurrentValues,
  template,
}: Pick<IEmailSectionProps, "currentValues" | "setCurrentValues" | "template">) => (
  <Grid item xs={12} mb={3}>
    <Typography fontSize={20} mb={1}>
      Descripción
    </Typography>
    <Typography fontSize={14} mb={2}>
      Edita el mensaje que visualizarán tus usuarios.
    </Typography>
    <TextField
      value={parseLineBreaksForInput(
        typeof currentValues.description === "undefined"
          ? template?.description || ""
          : currentValues?.description || ""
      )}
      multiline
      fullWidth
      size="small"
      onChange={(e) =>
        setCurrentValues({
          ...currentValues,
          description: e.target.value,
        })
      }
    />
  </Grid>
);

export const FooterSection = ({
  currentValues,
  setCurrentValues,
}: Pick<IEmailSectionProps, "currentValues" | "setCurrentValues">) => (
  <Grid item xs={12} mb={3}>
    <Typography fontSize={20} mb={1}>
      Footer
    </Typography>
    <Typography fontSize={14} mb={2}>
      Personaliza el pie de página de tus correos.
    </Typography>
    <Box display="flex" alignItems="center" mb={2}>
      <Typography fontSize={14} mr={2}>
        Habilitar footer
      </Typography>
      <Switch
        checked={!!currentValues.footerEnabled}
        onChange={(e) =>
          setCurrentValues({
            ...currentValues,
            footerEnabled: e.target.checked,
          })
        }
      />
    </Box>
    {currentValues.footerEnabled && (
      <ReactQuill
        theme="snow"
        value={currentValues.footerContent || ""}
        onChange={(content) =>
          setCurrentValues({ ...currentValues, footerContent: content })
        }
        modules={QUILL_MODULES}
      />
    )}
  </Grid>
);
